import { T, Var, useGT } from 'gt-react';
import type { ArTaskChannel, ArTaskRow } from '@erp/rpc';
import type { ColumnDef } from '@/lib/tanstack-table';
import { createSelectColumn } from '@/components/common/data-table/data-table-select-column';
import { DataTableColumnHeader } from '@/components/common/data-table/data-table-column-header';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CashReceiptLatestNoteCell } from '@/components/accounts-receivable/cash-receipt-detail/CashReceiptTaskCells';
import {
  formatLogTaskStatus,
  logTaskStatusClass,
} from '@/components/accounts-receivable/cash-receipt-detail/cashReceiptTaskFormat';
import { formatCurrency } from '@/utils/formatters';
import { formatDateOnlyString } from '@/utils/dateUtils';
import type { ArTaskRecord } from './arTasksPageState';

type Translate = ReturnType<typeof useGT>;

/** Tasks pending more than this many days are shown in red. */
const AR_TASKS_LATE_DAYS = 5;

export function isArTaskLate(daysPending: number | null | undefined): boolean {
  return daysPending != null && daysPending > AR_TASKS_LATE_DAYS;
}

export interface ArTasksColumnHandlers {
  onOpenReference: (record: ArTaskRecord) => void;
  onReassign: (row: ArTaskRow) => void;
}

export function formatArTaskAmount(amount: string | null): string {
  if (amount == null) return '—';
  return formatCurrency(Number(amount), { currency: 'USD' });
}

/**
 * The Last Note column: the newer of the task's own latest note and the
 * invoice's newest ERP note, which the feed copies onto the task (only a
 * dated one, so the cell can say when). A note written on the task the same
 * day as the ERP note wins.
 */
export function lastNote(
  row: Pick<ArTaskRow, 'latestNote' | 'invoiceNote'>,
  gt: Translate,
): ArTaskRow['latestNote'] {
  const { latestNote, invoiceNote } = row;
  if (!invoiceNote?.date) return latestNote;
  // Calendar dates on both sides: a same-day task note wins.
  if (
    latestNote &&
    latestNote.occurredAt.slice(0, 10) >= invoiceNote.date.slice(0, 10)
  ) {
    return latestNote;
  }
  return {
    body: invoiceNote.body,
    actorName: gt('Invoice note in the ERP', {
      context:
        'AR tasks Last Note author, the note came from the invoice in the ERP',
    }),
    occurredAt: invoiceNote.date,
  };
}

/** A task row's kind and, for a not-sent task, the customer's channels. */
type IssueRow = Pick<ArTaskRow, 'kind'> & Partial<Pick<ArTaskRow, 'channels'>>;

/** The only channel a not-sent task's customer uses, when there is exactly one. */
function soleChannel(row: IssueRow): ArTaskChannel | undefined {
  return row.channels?.length === 1 ? row.channels[0] : undefined;
}

export function issueLabel(row: IssueRow, gt: Translate): string {
  if (row.kind === 'to_invoice') {
    return gt('Shipped but not invoiced', {
      context: 'AR tasks issue, sales order shipped without an invoice',
    });
  }
  switch (soleChannel(row)) {
    case 'portal':
      return gt('Portal customer, no portal note', {
        context:
          'AR tasks issue, portal customer whose invoice has no note saying it was submitted to the portal',
      });
    case 'edi':
      return gt('EDI customer, no EDI invoice', {
        context:
          'AR tasks issue, EDI customer whose invoice was not sent or noted as sent by EDI',
      });
    case 'unknown':
      return gt('Not sent, channel unknown', {
        context:
          'AR tasks issue, invoice not sent to a new customer whose invoicing channel is not known yet',
      });
    default:
      return gt('Invoiced but not sent', {
        context: 'AR tasks issue, invoice saved but never sent',
      });
  }
}

function channelLabel(channel: ArTaskChannel, gt: Translate): string {
  switch (channel) {
    case 'email':
      return gt('Email', { context: 'AR tasks channel, invoices by email' });
    case 'edi':
      return gt('EDI', { context: 'AR tasks channel, invoices by EDI' });
    case 'portal':
      return gt('Portal', {
        context: 'AR tasks channel, invoices uploaded to a customer portal',
      });
    case 'unknown':
      return gt('Unknown', {
        context:
          'AR tasks channel, how the customer gets invoices is not known',
      });
  }
}

/** The customer's channels joined for the Channel column, or a dash. */
export function channelsLabel(
  channels: readonly ArTaskChannel[],
  gt: Translate,
): string {
  if (channels.length === 0) return '—';
  return channels.map((channel) => channelLabel(channel, gt)).join(', ');
}

/**
 * Bare numbers (Famous SONOs) get an SO / INV prefix so the kind is readable;
 * references that already carry one (CPO-000056, INV-41806) show as-is.
 */
export function referenceLabel(
  row: Pick<ArTaskRow, 'kind' | 'reference'>,
): string {
  const prefix = row.kind === 'to_invoice' ? 'SO' : 'INV';
  if (!row.reference) return prefix;
  return /^\d+$/.test(row.reference)
    ? `${prefix} ${row.reference}`
    : row.reference;
}

/**
 * The record's number, linking to it once it has synced into Gently; before
 * that the task only knows the ERP's number, so it shows as plain text.
 */
function ReferenceCell({
  row,
  onOpen,
}: {
  row: ArTaskRow;
  onOpen: (record: ArTaskRecord) => void;
}) {
  const { anchorId } = row;
  if (!anchorId) {
    return <span className="text-[13px]">{referenceLabel(row)}</span>;
  }
  return (
    <Button
      variant="link"
      className="h-auto p-0 text-[13px]"
      onClick={(event) => {
        event.stopPropagation();
        onOpen({ ...row, anchorId });
      }}
    >
      {referenceLabel(row)}
    </Button>
  );
}

/**
 * A sortable header (click sorts, shift-click adds a level) plus the column's
 * label on `meta.label`, which the View menu and the PDF export read.
 */
function sortableHeader(
  label: string,
  meta?: Record<string, unknown>,
): Pick<ColumnDef<ArTaskRow>, 'header' | 'meta'> {
  return {
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={label} />
    ),
    meta: { ...meta, label },
  };
}

export function getArTasksColumns(
  gt: Translate,
  handlers: ArTasksColumnHandlers,
): ColumnDef<ArTaskRow>[] {
  return [
    createSelectColumn<ArTaskRow>(gt),
    {
      id: 'reference',
      accessorFn: (row) => row.reference,
      ...sortableHeader(
        gt('Reference', {
          context: 'AR tasks column header, sales order or invoice number',
        }),
      ),
      cell: ({ row }) => (
        <ReferenceCell row={row.original} onOpen={handlers.onOpenReference} />
      ),
      size: 120,
    },
    {
      id: 'date',
      accessorFn: (row) => row.date,
      ...sortableHeader(
        gt('Date', {
          context: 'AR tasks column header, ship date or invoice date',
        }),
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[13px]">
          {row.original.date
            ? formatDateOnlyString(row.original.date, '—')
            : '—'}
        </span>
      ),
      size: 100,
    },
    {
      id: 'amount',
      accessorFn: (row) => row.amount,
      ...sortableHeader(
        gt('Amount', { context: 'AR tasks column header, amount' }),
        { align: 'right' },
      ),
      cell: ({ row }) => (
        <span className="block text-right text-[13px] tabular-nums">
          {formatArTaskAmount(row.original.amount)}
        </span>
      ),
      size: 120,
    },
    {
      id: 'customer',
      accessorFn: (row) => row.customerName,
      ...sortableHeader(
        gt('Customer', { context: 'AR tasks column header, customer' }),
      ),
      cell: ({ row }) => (
        <span className="block truncate text-[13px]">
          {row.original.customerName ?? '—'}
        </span>
      ),
      size: 200,
    },
    {
      id: 'issue',
      accessorFn: (row) => row.kind,
      ...sortableHeader(
        gt('Issue', {
          context: 'AR tasks column header, what is wrong with the record',
        }),
      ),
      cell: ({ row }) => (
        <span className="whitespace-nowrap text-[13px]">
          {issueLabel(row.original, gt)}
        </span>
      ),
      size: 200,
    },
    {
      id: 'pending',
      accessorFn: (row) => row.daysPending,
      ...sortableHeader(
        gt('Pending', {
          context: 'AR tasks column header, days since ship or invoice date',
        }),
      ),
      cell: ({ row }) => {
        const days = row.original.daysPending;
        if (days == null) return <span className="text-[13px]">—</span>;
        const late = isArTaskLate(days);
        return (
          <span
            className={`whitespace-nowrap text-[13px] ${late ? 'font-semibold text-red-700' : ''}`}
          >
            {days === 1 ? (
              <T context="AR tasks days pending, singular">1 day</T>
            ) : (
              <T context="AR tasks days pending, plural">
                <Var>{days}</Var> days
              </T>
            )}
          </span>
        );
      },
      size: 90,
    },
    {
      id: 'channel',
      accessorFn: (row) => row.channels.join(','),
      ...sortableHeader(
        gt('Channel', {
          context: 'AR tasks column header, how the invoice is delivered',
        }),
      ),
      cell: ({ row }) => (
        <span className="text-[13px]">
          {channelsLabel(row.original.channels, gt)}
        </span>
      ),
      size: 110,
    },
    {
      id: 'task',
      accessorFn: (row) => row.status,
      ...sortableHeader(
        gt('Task', { context: 'AR tasks column header, the task' }),
      ),
      cell: ({ row }) => (
        <div className="flex min-w-0 flex-col gap-0.5">
          <Badge
            variant="outline"
            className={`w-fit shrink-0 text-xs ${logTaskStatusClass(row.original.status)}`}
          >
            {formatLogTaskStatus(row.original.status, gt)}
          </Badge>
          {row.original.displayId && (
            <span className="truncate text-[11px] text-muted-foreground">
              {row.original.displayId}
            </span>
          )}
        </div>
      ),
      size: 130,
    },
    {
      id: 'assignee',
      accessorFn: (row) => row.assigneeName,
      ...sortableHeader(
        gt('Assignee', {
          context: 'AR tasks column header, who has the task',
        }),
      ),
      cell: ({ row }) => (
        <Button
          variant="ghost"
          size="sm"
          className={`-ml-2 h-7 px-2 text-[13px] font-normal ${
            row.original.assigneeName ? '' : 'text-muted-foreground'
          }`}
          onClick={(event) => {
            event.stopPropagation();
            handlers.onReassign(row.original);
          }}
        >
          {row.original.assigneeName ??
            gt('Unassigned', {
              context: 'AR tasks assignee cell, nobody has the task',
            })}
        </Button>
      ),
      size: 150,
    },
    {
      id: 'latestNote',
      accessorFn: (row) => lastNote(row, gt)?.body,
      ...sortableHeader(
        gt('Last Note', {
          context: 'AR tasks column header, newest note on the task',
        }),
      ),
      cell: ({ row }) => (
        <CashReceiptLatestNoteCell note={lastNote(row.original, gt)} wrap />
      ),
      size: 240,
    },
  ];
}

/** A column's display label: its `meta.label`, else a plain-string header. */
function columnLabel(column: ColumnDef<ArTaskRow>): string | undefined {
  const { meta } = column;
  if (
    typeof meta === 'object' &&
    meta !== null &&
    'label' in meta &&
    typeof meta.label === 'string'
  ) {
    return meta.label;
  }
  return typeof column.header === 'string' ? column.header : undefined;
}

/**
 * Column id → its label, for the View (column visibility) menu. Reads the
 * column definitions themselves, so there are no second strings to keep in
 * sync.
 */
export function arTasksColumnLabels(
  columns: ColumnDef<ArTaskRow>[],
): Record<string, string> {
  return Object.fromEntries(
    columns.flatMap((column) => {
      const label = columnLabel(column);
      return column.id && label ? [[column.id, label]] : [];
    }),
  );
}
